import useSWR from 'swr';
import { fetchEndpointData } from '@/utils/fetch.client';

/**
 * A pending CLI sign-in request, as shown on the approval page.
 *
 * `deviceLabel` is reported by the requesting machine and is therefore untrusted — render it as
 * text, never as a link, and label it as self-reported. `requestIp` is captured server-side and
 * is the one field the requester cannot forge.
 */
export interface CliDeviceRequest {
  userCode: string;
  deviceLabel: string | null;
  requestIp: string | null;
  expiresAt: string;
}

interface DecideResponse {
  approved: boolean;
}

/**
 * Look up a pending request by the code the human typed or followed.
 *
 * Unknown, expired and already-decided codes are all a 404 — the backend deliberately does not
 * distinguish them, so this cannot be used to discover which codes exist. `null` means "no
 * code to look up yet", which suspends the request rather than firing a doomed one.
 */
export function useCliDeviceRequest(userCode: string | null) {
  const key = userCode ? `/api/v1/cli/device/${encodeURIComponent(userCode)}` : null;
  const { data, error, isLoading } = useSWR(key, () => fetchEndpointData<CliDeviceRequest>(key as string), {
    // A pending request is short-lived and single-use: re-fetching it when the window regains
    // focus would show a confusing "expired" state right after a successful approval.
    revalidateOnFocus: false,
    shouldRetryOnError: false,
  });
  return { request: data, error, isLoading };
}

/**
 * Approve or deny a pending request.
 *
 * Always a POST, never a side effect of loading the page. That is the phishing defence: approval
 * has to be a deliberate act taken after comparing the code on screen with the one in the
 * terminal.
 */
export async function decideCliDeviceRequest(userCode: string, approved: boolean, teamId?: string) {
  return fetchEndpointData<DecideResponse>(`/api/v1/cli/device/${encodeURIComponent(userCode)}/decide`, {
    method: 'POST',
    body: { approved, teamId },
  });
}
